import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Chip,
  Button,
  Tooltip,
  IconButton
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CloseIcon from '@mui/icons-material/Close';
import { themeColors } from '../common/constants';

// Component to display active filters at the top of the data table
const ActiveFilterDisplay = ({ activeFilters, onClearFilters, resultCount }) => {
  // Return null if no active filters
  if (!activeFilters || Object.keys(activeFilters).length === 0) {
    return null;
  }
  
  // Remove keys with null/undefined values
  const filteredEntries = Object.entries(activeFilters).filter(
    ([key, value]) => value !== null && value !== undefined
  );
  
  // Return null if no valid filters after filtering out nulls
  if (filteredEntries.length === 0) {
    return null;
  }
  
  // Format filter names for display
  const formatFilterName = (key) => {
    const nameMap = {
      'office': 'Office',
      'sub_office': 'Sub-Office',
      'start_date': 'Start Date',
      'end_date': 'End Date',
      'clientType': 'Client Type',
      'gender': 'Gender',
      'minAge': 'Min Age',
      'maxAge': 'Max Age',
      'minSqdScore': 'Min SQD Score',
      'maxSqdScore': 'Max SQD Score'
    };
    
    return nameMap[key] || key;
  };
  
  // Format filter values for display
  const formatFilterValue = (key, value) => {
    // Format dates to localized string
    if (key === 'start_date' || key === 'end_date') {
      try {
        // Parse the date string and format it
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      } catch (error) {
        console.error('Error formatting date:', error);
      }
    }
    
    return value;
  };
  
  return (
    <Paper 
      elevation={0}
      variant="outlined"
      sx={{ 
        p: 2, 
        mb: 3, 
        bgcolor: '#FAFAFA',
        border: `1px solid ${themeColors.primary}20`,
        borderRadius: 1
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterAltIcon sx={{ mr: 1, color: themeColors.primary }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: themeColors.primary }}>
            Filtered Results
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ mr: 2, color: 'text.secondary' }}>
            {resultCount !== undefined ? (
              <>{resultCount} {resultCount === 1 ? 'result' : 'results'} found</>
            ) : (
              <>Showing filtered results</>
            )}
          </Typography>
          
          <Button 
            size="small" 
            variant="outlined"
            onClick={onClearFilters}
            startIcon={<CloseIcon fontSize="small" />}
            sx={{ 
              borderColor: themeColors.secondary,
              color: themeColors.secondary,
              '&:hover': {
                backgroundColor: `${themeColors.secondary}15`,
                borderColor: themeColors.secondary
              }
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {filteredEntries.map(([key, value]) => (
          <Chip
            key={key}
            label={`${formatFilterName(key)}: ${formatFilterValue(key, value)}`}
            size="small"
            sx={{ 
              bgcolor: `${themeColors.primary}15`,
              color: themeColors.primary
            }}
          />
        ))}
      </Box>
    </Paper>
  );
};

export default ActiveFilterDisplay;